import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { LogoUNEA } from '../components/common/Logo';

const STARS = [1, 2, 3, 4, 5];
const LABELS = { 1: 'Muy mala', 2: 'Mala', 3: 'Regular', 4: 'Buena', 5: 'Excelente' };

export default function SurveyPage() {
  const { token }          = useParams();
  const [params]           = useSearchParams();
  const initialRating      = Number(params.get('rating')) || 0;

  const [survey,   setSurvey]   = useState(null);   // datos del ticket
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [rating,   setRating]   = useState(initialRating);
  const [hovered,  setHovered]  = useState(0);
  const [comment,  setComment]  = useState('');
  const [sending,  setSending]  = useState(false);
  const [done,     setDone]     = useState(false);

  useEffect(() => {
    fetch(`/api/survey/${token}`)
      .then(r => r.json())
      .then(data => {
        if (data.message) { setError(data.message); }
        else { setSurvey(data); if (data.already_rated) setDone(true); }
        setLoading(false);
      })
      .catch(() => { setError('Error al cargar la encuesta'); setLoading(false); });
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!rating) return;
    setSending(true);
    try {
      const res = await fetch(`/api/survey/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating, comment }),
      });
      const data = await res.json();
      if (res.ok) setDone(true);
      else setError(data.message || 'Error al enviar');
    } catch {
      setError('Error al enviar la valoración');
    } finally {
      setSending(false);
    }
  }

  const active = hovered || rating;

  return (
    <div style={{minHeight:'100vh',background:'#F0F2F5',display:'flex',flexDirection:'column',
      alignItems:'center',justifyContent:'center',padding:'32px 16px',fontFamily:'Arial,Helvetica,sans-serif'}}>

      <div style={{width:'100%',maxWidth:480,background:'#fff',borderRadius:8,
        boxShadow:'0 2px 12px rgba(0,0,0,.08)',overflow:'hidden'}}>

        {/* Cabecera */}
        <div style={{padding:'24px 32px 20px',borderBottom:'1px solid #E5E7EB',textAlign:'center'}}>
          <LogoUNEA height={36} style={{display:'inline-block',marginBottom:8}}/>
          <div style={{fontSize:12,color:'#9CA3AF',marginTop:4}}>HelpDesk TI</div>
        </div>

        <div style={{padding:'32px 36px'}}>
          {loading && (
            <div style={{textAlign:'center',color:'#9CA3AF',padding:'24px 0'}}>Cargando…</div>
          )}

          {!loading && error && (
            <div style={{textAlign:'center'}}>
              <div style={{fontSize:36,marginBottom:12}}>🔍</div>
              <div style={{fontSize:15,fontWeight:600,color:'#374151',marginBottom:8}}>Encuesta no disponible</div>
              <div style={{fontSize:13,color:'#9CA3AF'}}>{error}</div>
            </div>
          )}

          {!loading && !error && done && (
            <div style={{textAlign:'center'}}>
              <div style={{fontSize:48,marginBottom:16}}>🎉</div>
              <div style={{fontSize:18,fontWeight:700,color:'#111827',marginBottom:8}}>
                {survey?.already_rated && survey?.rating
                  ? `Tu valoración: ${'★'.repeat(survey.rating)}${'☆'.repeat(5 - survey.rating)}`
                  : '¡Gracias por tu valoración!'}
              </div>
              <div style={{fontSize:13,color:'#6B7280',lineHeight:1.6}}>
                Tu opinión nos ayuda a mejorar el servicio de soporte.<br/>
                Puedes cerrar esta ventana.
              </div>
            </div>
          )}

          {!loading && !error && !done && survey && (
            <form onSubmit={handleSubmit}>
              {/* Info ticket */}
              <div style={{background:'#F9FAFB',border:'1px solid #E5E7EB',borderRadius:6,
                padding:'12px 16px',marginBottom:24}}>
                <div style={{fontSize:10,fontWeight:700,letterSpacing:.4,textTransform:'uppercase',
                  color:'#9CA3AF',marginBottom:4}}>Ticket resuelto</div>
                <div style={{fontSize:13,fontWeight:600,color:'#111827'}}>{survey.title}</div>
                <div style={{fontSize:11,color:'#9CA3AF',marginTop:2,fontFamily:'monospace'}}>{survey.ref}</div>
              </div>

              {/* Pregunta */}
              <div style={{textAlign:'center',marginBottom:20}}>
                <div style={{fontSize:15,fontWeight:600,color:'#111827',marginBottom:18}}>
                  ¿Cómo valorarías la atención recibida?
                </div>
                {/* Estrellas */}
                <div style={{display:'flex',justifyContent:'center',gap:8,marginBottom:8}}>
                  {STARS.map(n => (
                    <button key={n} type="button"
                      onClick={() => setRating(n)}
                      onMouseEnter={() => setHovered(n)}
                      onMouseLeave={() => setHovered(0)}
                      style={{width:48,height:48,border:'none',background:'none',cursor:'pointer',
                        fontSize:36,lineHeight:1,padding:0,
                        color: n <= active ? '#F59E0B' : '#D1D5DB',
                        transform: n <= active ? 'scale(1.15)' : 'scale(1)',
                        transition:'all .12s'}}>
                      ★
                    </button>
                  ))}
                </div>
                {active > 0 && (
                  <div style={{fontSize:12,color:'#6B7280',height:16,transition:'opacity .1s'}}>
                    {LABELS[active]}
                  </div>
                )}
              </div>

              {/* Comentario opcional */}
              <div style={{marginBottom:24}}>
                <label style={{fontSize:12,fontWeight:600,color:'#374151',display:'block',marginBottom:6}}>
                  Comentario <span style={{fontWeight:400,color:'#9CA3AF'}}>(opcional)</span>
                </label>
                <textarea
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  placeholder="¿Qué mejorarías? ¿Algo que destacar?"
                  rows={3}
                  style={{width:'100%',boxSizing:'border-box',padding:'10px 12px',fontSize:13,
                    border:'1px solid #E5E7EB',borderRadius:6,resize:'vertical',
                    color:'#111827',outline:'none',fontFamily:'inherit'}}
                />
              </div>

              {/* Botón */}
              <button type="submit" disabled={!rating || sending}
                style={{width:'100%',height:42,background: rating ? '#0277BD' : '#E5E7EB',
                  color: rating ? '#fff' : '#9CA3AF',border:'none',borderRadius:6,
                  fontSize:14,fontWeight:600,cursor: rating ? 'pointer' : 'not-allowed',
                  transition:'all .15s'}}>
                {sending ? 'Enviando…' : 'Enviar valoración'}
              </button>
            </form>
          )}
        </div>

        <div style={{padding:'12px 32px',borderTop:'1px solid #E5E7EB',background:'#F9FAFB',
          textAlign:'center',fontSize:11,color:'#9CA3AF'}}>
          Este formulario ha sido enviado automáticamente por HelpDesk TI — UNEATLANTICO
        </div>
      </div>
    </div>
  );
}
